import { IBAN_WRONG, ibanOk } from '../../auth/company';
import type { FormErrors, FormValues } from '../../auth/CompanyFields';
import { t, tl } from '../../i18n/t';

// Prüfungen des Personalfragebogens, wie auf dem Server (dort verbindlich). Meldungen direkt am Feld, in der Sprache der App.

export function steuerIdOk(input: string): boolean {
  const s = input.replace(/\s+/g, '');
  if (!/^[1-9]\d{10}$/.test(s)) return false;
  let p = 10;
  for (let i = 0; i < 10; i++) {
    let d = (Number(s[i]) + p) % 10;
    if (d === 0) d = 10;
    p = (d * 2) % 11;
  }
  const c = 11 - p;
  return (c === 10 ? 0 : c) === Number(s[10]);
}

export function svNummerOk(input: string): boolean {
  const m = /^(\d{8})([A-Z])(\d{3})$/.exec(input.replace(/\s+/g, '').toUpperCase());
  if (!m) return false;
  const d = m[1]! + String(m[2]!.charCodeAt(0) - 64).padStart(2, '0') + m[3]!.slice(0, 2);
  const w = [2, 1, 2, 5, 7, 1, 2, 1, 2, 1, 2, 1];
  let n = 0;
  for (let i = 0; i < 12; i++) {
    const q = Number(d[i]) * w[i]!;
    n += Math.floor(q / 10) + (q % 10);
  }
  return n % 10 === Number(m[3]![2]);
}

export type UploadKind = 'ausweis_vorne' | 'ausweis_hinten' | 'aufenthaltstitel' | 'immatrikulation';

// Wer welchen Teil des Personalfragebogens ausfüllt: „alles“ der Mandant selbst; mit Link trägt der Mandant Name und
// Beschäftigung ein („firma“), der Mitarbeiter alles andere („mitarbeiter“, den Namen darf er korrigieren)
export type Part = 'alles' | 'firma' | 'mitarbeiter';
export const JOB_KEYS = new Set([
  'eintritt',
  'befristetBis',
  'taetigkeit',
  'art',
  'stunden',
  'tage',
  'urlaub',
  'brutto',
  'stundenlohn',
  'weitere',
  'rvBefreiung',
]);
const NAME_KEYS = new Set(['vorname', 'nachname']);

export function inPart(k: string, part: Part): boolean {
  if (part === 'alles' || NAME_KEYS.has(k)) return true;
  return part === 'firma' ? JOB_KEYS.has(k) : !JOB_KEYS.has(k);
}

// Nur die Meldungen zu Feldern, die in diesem Teil vorkommen
export function errorsOf(e: FormErrors, part: Part): FormErrors {
  return Object.fromEntries(Object.entries(e).filter(([k]) => inPart(k, part)));
}

// Welche Felder und Dateien gerade Pflicht sind (hängt von Beschäftigungsart, Ausweis und SV-Nummer ab). Ausweis-Fotos
// nur mit Einverständnis zur Kopie (ausweisKopie, § 20 Abs. 2 PAuswG; wie der Server).
export function rules(v: FormValues, svKeine: boolean, ausweisKopie = false) {
  const mini = v.art === 'Minijob';
  const pass = v.ausweisart === 'Reisepass';
  const ws = v.art === 'Werkstudent';
  return {
    mini,
    pass,
    ws,
    required: new Set([
      'vorname',
      'nachname',
      'geburtsdatum',
      'geburtsort',
      'staatsangehoerigkeit',
      'strasse',
      'plz',
      'ort',
      'ausweisart',
      'eintritt',
      'taetigkeit',
      'art',
      'stunden',
      'steuerId',
      'krankenkasse',
      ...(!v.stundenlohn?.trim() ? ['brutto'] : []),
      ...(!v.brutto?.trim() ? ['stundenlohn'] : []),
      ...(mini ? ['weitere', 'rvBefreiung'] : ['steuerklasse']),
      ...(svKeine ? ['geschlecht', 'geburtsland'] : ['svNummer']),
    ]),
    files: {
      ausweis_vorne: ausweisKopie,
      ausweis_hinten: ausweisKopie && !pass,
      aufenthaltstitel: false,
      immatrikulation: ws,
    } as Record<UploadKind, boolean>,
  };
}

// Bezeichnungen bleiben Deutsch (tl); übersetzt wird beim Anzeigen mit t()
export const LABEL: Record<string, string> = {
  vorname: tl('Vorname'),
  nachname: tl('Nachname'),
  geburtsname: tl('Geburtsname'),
  geschlecht: tl('Geschlecht'),
  geburtsdatum: tl('Geburtsdatum'),
  geburtsort: tl('Geburtsort'),
  geburtsland: tl('Geburtsland'),
  staatsangehoerigkeit: tl('Staatsangehörigkeit'),
  strasse: tl('Straße und Hausnummer'),
  plz: tl('PLZ'),
  ort: tl('Ort'),
  ausweisart: tl('Ausweisart'),
  eintritt: tl('Eintritt'),
  taetigkeit: tl('Tätigkeit'),
  art: tl('Beschäftigungsart'),
  stunden: tl('Wöchentliche Arbeitsstunden'),
  brutto: tl('Brutto pro Monat'),
  stundenlohn: tl('Stundenlohn'),
  weitere: tl('Weitere Beschäftigung bei einem anderen Arbeitgeber?'),
  rvBefreiung: tl('Befreiung von der Rentenversicherung'),
  steuerId: tl('Steuer-ID'),
  steuerklasse: tl('Steuerklasse'),
  svNummer: tl('Sozialversicherungsnummer'),
  krankenkasse: tl('Krankenkasse'),
  iban: 'IBAN',
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// today = 'YYYY-MM-DD'; fileLabel: Bezeichnung der Datei in der Sprache der App
export function validate(
  v: FormValues,
  svKeine: boolean,
  // hochgeladene (oder bei einer Wiedereinstellung schon vorhandene) Dateien
  files: Partial<Record<UploadKind, unknown>>,
  today: string,
  fileLabel: (k: UploadKind) => string,
  ausweisKopie = false,
): FormErrors {
  const r = rules(v, svKeine, ausweisKopie);
  const e: FormErrors = {};
  const val = (k: string) => (v[k] ?? '').trim();
  for (const k of r.required) {
    if (!val(k))
      e[k] =
        k === 'brutto'
          ? t('Bitte Brutto pro Monat oder Stundenlohn angeben.')
          : t('Bitte „{label}“ ausfüllen.', { label: t(LABEL[k] ?? k) });
  }
  if (e.stundenlohn && e.brutto) delete e.stundenlohn;
  const set = (k: string, ok: boolean, msg: string) => {
    if (!e[k] && val(k) && !ok) e[k] = msg;
  };
  const y = Number(today.slice(0, 4));
  const gd = val('geburtsdatum');
  set(
    'geburtsdatum',
    DATE_RE.test(gd) && gd >= `${y - 100}${today.slice(4)}` && gd <= `${y - 13}${today.slice(4)}`,
    t('Bitte das Geburtsdatum prüfen: Der Mitarbeiter muss zwischen 13 und 100 Jahre alt sein.'),
  );
  set('plz', /^\d{5}$/.test(val('plz')), t('Die Postleitzahl hat 5 Ziffern.'));
  set('email', /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val('email')), t('Bitte eine gültige E-Mail-Adresse eingeben.'));
  const ein = val('eintritt');
  set('eintritt', DATE_RE.test(ein) && ein >= '1950-01-01' && ein <= '2100-12-31', t('Bitte das Eintrittsdatum prüfen.'));
  set(
    'befristetBis',
    DATE_RE.test(val('befristetBis')) && (!ein || val('befristetBis') > ein),
    t('Das Ende der Befristung muss nach dem Eintritt liegen.'),
  );
  set(
    'steuerId',
    steuerIdOk(val('steuerId')),
    t('Die Steuer-ID hat 11 Ziffern (z. B. 86 095 742 719). Bitte prüfen Sie sie noch einmal.'),
  );
  if (!svKeine) {
    const sv = val('svNummer').replace(/\s+/g, '').toUpperCase();
    set(
      'svNummer',
      svNummerOk(sv),
      /^\d{8}[A-Z]\d{3}$/.test(sv)
        ? t(
            'Die Prüfziffer der Sozialversicherungsnummer passt nicht. Bitte noch einmal vom Ausweis abschreiben oder „Noch keine“ ankreuzen.',
          )
        : t('Die Sozialversicherungsnummer hat 12 Zeichen, z. B. 65 170839 J 003. Bitte prüfen oder „Noch keine“ ankreuzen.'),
    );
  }
  set('iban', ibanOk(val('iban')), t(IBAN_WRONG));
  for (const k of ['stunden', 'tage', 'urlaub', 'brutto', 'stundenlohn', 'kinderfreibetraege', 'kinder']) {
    set(
      k,
      Number.isFinite(Number(val(k).replace(',', '.'))) && Number(val(k).replace(',', '.')) >= 0,
      t('Bitte eine Zahl ab 0 eingeben.'),
    );
  }
  for (const k of Object.keys(r.files) as UploadKind[]) {
    if (r.files[k] && !files[k]) e[k] = t('Bitte noch hochladen: {files}', { files: fileLabel(k) });
  }
  return e;
}
